import {
  MEDIA_MESSAGE_TYPES,
  messageLabelsType
} from "@/shared/constants/messages";
import type { TFunction } from "i18next";

import {
  ATTACHMENT_STATUS,
  type AttachmentDiscount,
  type AttachmentStatus,
  type MessageAttachmentMedia
} from "@/features/chat/shared/types/media/media";
import type {
  ChatMessage,
  ChatMessageType,
  LiveOfferStatus
} from "@/features/chat/shared/types";

export interface MediaCounts {
  image: number;
  video: number;
}

export const VIDEO_TYPES = ["file", "video"];

const DISCOUNT_DURATION_MS: Record<string, number> = {
  three_hours: 3 * 60 * 60 * 1000,
  six_hours: 6 * 60 * 60 * 1000,
  twelve_hours: 12 * 60 * 60 * 1000,
  one_day: 24 * 60 * 60 * 1000,
  tw_days: 2 * 24 * 60 * 60 * 1000,
  seven_days: 7 * 24 * 60 * 60 * 1000
};

const formatPrice = (amount: number, currency: string): string => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency
  }).format(amount);
};

/** Extracts price and currency from either old format (number in cents) or new format ({ amount, currency }) */
const extractPrice = (
  price: unknown
): { amount: number; currency: string; formatted: string } => {
  if (price && typeof price === "object" && "amount" in price) {
    const p = price as { amount: number; currency: string };
    const amount = p.amount / 100;
    return {
      amount,
      currency: p.currency,
      formatted: formatPrice(amount, p.currency)
    };
  }
  if (typeof price === "number") {
    const amount = price / 100;
    return { amount, currency: "EUR", formatted: formatPrice(amount, "EUR") };
  }
  return { amount: 0, currency: "EUR", formatted: formatPrice(0, "EUR") };
};

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const computePromoText = (
  discount: AttachmentDiscount | null | undefined,
  messageDate: string,
  t: TFunction
): string | undefined => {
  if (!discount) return undefined;

  const durationMs = DISCOUNT_DURATION_MS[discount.duration];
  if (!durationMs) return undefined;

  const remaining = new Date(messageDate).getTime() + durationMs - Date.now();
  if (remaining <= 0) return undefined;

  const days = Math.floor(remaining / DAY_MS);
  if (days >= 1) {
    return t("creator_chat_push_promo_days", { count: days });
  }

  const hours = Math.floor(remaining / HOUR_MS);
  if (hours >= 1) {
    return t("creator_chat_push_promo_hours", { count: hours });
  }

  const minutes = Math.max(1, Math.ceil(remaining / (60 * 1000)));
  return t("creator_chat_push_promo_minutes", { count: minutes });
};

const getMediaCounts = (media?: MessageAttachmentMedia[]): MediaCounts => {
  if (!media?.length) return { image: 0, video: 0 };

  return media.reduce<MediaCounts>(
    (counts, item) => {
      if (VIDEO_TYPES.includes(item.type)) {
        counts.video++;
      } else {
        counts.image++;
      }
      return counts;
    },
    { image: 0, video: 0 }
  );
};

export const isMediaMessage = (message: { type: string }): boolean =>
  MEDIA_MESSAGE_TYPES.includes(message.type as ChatMessageType);

export const getMessageStatus = (
  message: { type: string; _failed?: boolean; _sending?: boolean },
  position: "left" | "right"
): "sending" | "sent" | "failed" => {
  if (position === "right") {
    if (message._sending) return "sending";
    if (message._failed) return "failed";
  }
  return "sent";
};

export const getReadStatus = (
  message: { sender_id: string; read_at: string | null },
  currentUserId: string
): "read" | "unread" | undefined => {
  if (message.sender_id !== currentUserId) {
    return undefined;
  }

  if (message.read_at) {
    return "read";
  }

  return "unread";
};

type BuildMediaOfferMessage = {
  type: string;
  date: string;
  attachment?: {
    status?: string;
    price?: unknown;
    old_price?: unknown;
    discount?: AttachmentDiscount | null;
    teaser?: MessageAttachmentMedia | null;
    media?: MessageAttachmentMedia[];
    duration?: number;
    url?: string;
  };
};

export const buildMediaOffer = (
  message: BuildMediaOfferMessage,
  t: TFunction
) => {
  if (!isMediaMessage(message)) return undefined;

  const attachment = message.attachment;
  const status = (attachment?.status ??
    ATTACHMENT_STATUS.waiting_for_payment) as AttachmentStatus;
  const pricing = extractPrice(attachment?.price);

  return {
    price: pricing.amount,
    currency: pricing.currency,
    formattedPrice: pricing.formatted,
    status,
    isPaid:
      status === ATTACHMENT_STATUS.paid ||
      status === ATTACHMENT_STATUS.delivered ||
      status === ATTACHMENT_STATUS.purchased,
    images: attachment?.media?.map((attachmentMedia) => ({
      id: attachmentMedia.id,
      url: attachmentMedia.url ?? "",
      thumbnail_url: attachmentMedia.thumbnail_url ?? "",
      type: attachmentMedia.type,
      position: attachmentMedia.position,
      duration: attachmentMedia.duration
    })),
    teaser: attachment?.teaser
      ? {
          id: attachment.teaser.id,
          url: attachment.teaser.url ?? "",
          thumbnail_url: attachment.teaser.thumbnail_url ?? "",
          type: attachment.teaser.type,
          position: attachment.teaser.position ?? 0,
          duration: attachment.teaser.duration
        }
      : undefined,
    mediaCounts: getMediaCounts(attachment?.media),
    messageTypeLabel: message.type,
    promoText: computePromoText(attachment?.discount, message.date, t)
  };
};

export const buildLiveOffer = (message: ChatMessage, t: TFunction) => {
  if (message.type !== "live") return undefined;

  // Support both message.live and message.attachment for live data
  const live = message.live;
  const attachment = message.attachment;

  if (!live && !attachment?.duration) return undefined;

  const bookingId = live?.booking_id || attachment?.id;
  const duration = live?.duration
    ? live.duration
    : (attachment?.duration ?? 0) / 60;
  const price = live?.price
    ? live.price
    : typeof attachment?.price === "number"
    ? attachment.price
    : attachment?.price?.amount ?? 0;
  const pricing = extractPrice(price);
  const bookingUrl = attachment?.url;
  const isPaid = live?.offer_status === "paid" || !!bookingUrl;

  return {
    status: (live?.offer_status ?? "paid") as LiveOfferStatus,
    pricing,
    duration: `${duration} min`,
    bookingId,
    bookingUrl,
    actions: {
      onDeliverMedia:
        isPaid && bookingUrl
          ? () => window.open(bookingUrl, "_self")
          : undefined
    },
    content: {
      buttons: {
        deliver: isPaid
          ? t("creators_messaging_live_show_start_button")
          : undefined
      },
      statusLabels: {
        default: t("creators_messaging_live_show_status_label"),
        proposed: t(
          "NSFW_Live_1_1_creator_calendar__steptwo__service_form_price"
        ),
        pending: t("creators_messaging_live_show_pending_status"),
        accepted: t("creators_messaging_live_show_accepted_status")
      },
      durationLabel: t(
        "NSFW_Live_1_1_creator_calendar_steptwo_service_form_label"
      ),
      warning: isPaid ? t("creators_messaging_live_show_warning") : undefined
    }
  };
};
